import type { StateAction } from "@entrilia/shared/types";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import type { EmailTrackingStatus, SearchSentEmailsRequest } from "../../../api/types/emailsTypes";

export interface EmailDeliveryLogState {
  dateRange?: DescriptiveDateRange | undefined;
  statusFilter: string;
  searchTerm: string;
  filterParams: Omit<SearchSentEmailsRequest, "range">;
}

export const emailTrackingStatusFilterValues = [
  "To Be Sent",
  "Sent",
  "Delivered",
  "Opened",
  "Delivered & Opened",
  "Bounced",
  "Not Tracked",
];

type EmailTrackingStatusFilterValue = (typeof emailTrackingStatusFilterValues)[number];

export const emailTrackingStatusFilterMap: Record<EmailTrackingStatusFilterValue, EmailTrackingStatus[]> = {
  "To Be Sent": ["ToBeSent"],
  Sent: ["Sent"],
  Delivered: ["Delivered"],
  Opened: ["Opened"],
  Bounced: ["PermanentFailure"],
  "Delivered & Opened": ["Delivered", "Opened"],
  "Not Tracked": ["NotTracked"],
};

export const getInitialEmailDeliveryLogState = (): EmailDeliveryLogState => ({
  statusFilter: "",
  searchTerm: "",
  filterParams: {},
});

const getFilterParams = (state: EmailDeliveryLogState): Omit<SearchSentEmailsRequest, "range"> => ({
  query: state.searchTerm || undefined,
  sentAtFrom: state.dateRange?.from?.toISOString(),
  sentAtTo: state.dateRange?.to?.toISOString(),
  statuses: emailTrackingStatusFilterMap[state.statusFilter],
});

// Actions

export const updateStatusFilterAction =
  (statusFilter: string): StateAction<EmailDeliveryLogState> =>
  (state) => {
    const newState = { ...state, statusFilter };
    return { ...newState, filterParams: getFilterParams(newState) };
  };

export const updateDatesFilterAction =
  (dateRange: DescriptiveDateRange): StateAction<EmailDeliveryLogState> =>
  (state) => {
    const newState = { ...state, dateRange };
    return { ...newState, filterParams: getFilterParams(newState) };
  };

export const searchItemsAction =
  (searchTerm: string): StateAction<EmailDeliveryLogState> =>
  (state) => {
    const newState = { ...state, searchTerm };
    return { ...newState, filterParams: getFilterParams(newState) };
  };

export const clearFiltersAction = (): StateAction<EmailDeliveryLogState> => (state) => ({
  ...state,
  dateRange: undefined,
  statusFilter: "",
  searchTerm: "",
  filterParams: {},
});
